import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import Script from 'next/script'
import type {FC} from 'react'
import {useCallback} from 'react'
import {useWindowSize} from 'react-use'
import {Container} from 'src/common/Container'
import type {Context} from 'src/common/types'
import {TourOverlay, TourSelector, TourViewer} from './components'
import {LeadIdProvider} from './context/LeadIdContext'
import {useProductTour, useProductTourAnalytics} from './hooks'

/**
 * Props for `ProductTourSection`.
 */
export type ProductTourSectionProps = SliceComponentProps<Content.ProductTourSectionSlice> & {
  context: Context
}

/**
 * Inner component that uses the LeadIdContext
 */
const ProductTourSectionContent: FC<ProductTourSectionProps> = ({slice, context}) => {
  const totalTours = slice.primary.tours?.length || 0
  const {state, setActiveTour, startTour, handleLeadSubmit} = useProductTour(totalTours)
  const analytics = useProductTourAnalytics(context.pageUid || 'unknown')
  const isMobile = useWindowSize().width < 900

  const handleStartTour = useCallback(() => {
    analytics.startTour(slice.primary.start_tour_button_label || '')
    startTour()
  }, [analytics, slice.primary.start_tour_button_label, startTour])

  const handleTourSelect = useCallback(
    (index: number) => {
      setActiveTour(index)
      const tourName = slice.primary.tours[index]?.product || `Tour ${index + 1}`
      analytics.selectTour(index, tourName)
    },
    [setActiveTour, analytics, slice.primary.tours],
  )

  if (!isFilled.group(slice.primary.tours)) {
    return null
  }

  if (isMobile) {
    return (
      <Container className="px-5 !pt-[180px] pb-8">
        <div className="flex h-[467px] w-full flex-col items-center justify-start gap-4">
          <PrismicNextImage field={slice.primary.mobile?.[0]?.asset} className="size-10" />
          <PrismicRichText
            components={{
              heading1: ({children}) => (
                <h1 className="text-center text-[32px] font-medium leading-[normal] text-moss-black">{children}</h1>
              ),
            }}
            field={slice.primary.mobile?.[0]?.title}
          />
          <PrismicRichText
            field={slice.primary.mobile?.[0]?.description}
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-l max-w-[335px] text-center text-moss-grey-500">{children}</p>
              ),
            }}
          />
        </div>
      </Container>
    )
  }

  const activeTourData = slice.primary.tours[state.activeTour]

  return (
    <Container
      backgroundColor="Grey/100"
      className="px-5 pb-20 pt-[122px] max-md:hidden md:px-10 md:pb-[120px] md:pt-[184px] lg:px-5 xl:px-0"
    >
      <Script async src="https://js.storylane.io/js/v2/storylane.js" />
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-16">
        {/* Header Section */}
        <div className="px-5 md:px-10 xl:px-0">
          <PrismicRichText
            field={slice.primary.heading}
            components={{
              heading1: ({children}) => (
                <h1 className="text-center text-[48px] font-medium leading-[56px] text-moss-black">{children}</h1>
              ),
              strong: ({children}) => <span className="text-moss-black/[60%]">{children}</span>,
            }}
          />
        </div>

        {/* Tour Cards & Viewer Section */}
        <div className="flex flex-col gap-16">
          <TourSelector tours={slice.primary.tours} activeTour={state.activeTour} onTourSelect={handleTourSelect} />

          <TourViewer
            tour={activeTourData}
            tourStarted={state.tourStarted}
            loadDemo={!state.showLeadForm}
            pageUid={context.pageUid || 'unknown'}
          >
            <TourOverlay
              showLeadForm={state.showLeadForm}
              dialogContent={slice.primary.dialog}
              startTourButtonLabel={slice.primary.start_tour_button_label || undefined}
              context={context}
              onStartTour={handleStartTour}
              onLeadSubmit={handleLeadSubmit}
            />
          </TourViewer>
        </div>
      </div>
    </Container>
  )
}

const ProductTourSection: FC<ProductTourSectionProps> = (props) => (
  <LeadIdProvider>
    <ProductTourSectionContent {...props} />
  </LeadIdProvider>
)

export default ProductTourSection
